import React, {
  createContext,
  useContext,
  useState,
  useCallback,
  ReactNode,
} from 'react';
import {
  ConfirmDialog,
  DialogVariant,
} from '@/components/ui/ConfirmDialog.tsx';

interface ConfirmationState {
  isOpen: boolean;
  title: string;
  description: string | React.ReactNode;
  confirmText?: string;
  cancelText?: string;
  loadingText?: string;
  variant: DialogVariant;
  isLoading: boolean;
  resolve?: (value: boolean) => void;
  onConfirmCallback?: () => Promise<void> | void;
}

interface PromiseDialogOptions {
  title: string;
  description: string | React.ReactNode;
  variant?: DialogVariant;
  confirmText?: string;
  cancelText?: string;
}

interface CallbackDialogOptions {
  title: string;
  description: string | React.ReactNode;
  variant?: DialogVariant;
  confirmText?: string;
  cancelText?: string;
  loadingText?: string;
  onConfirm: () => Promise<void> | void;
  onCancel?: () => void;
}

interface ConfirmationDialogContextType {
  showDialog: (options: PromiseDialogOptions) => Promise<boolean>;
  showDialogWithCallback: (options: CallbackDialogOptions) => void;
}

const ConfirmDialogContext = createContext<
  ConfirmationDialogContextType | undefined
>(undefined);

interface ConfirmationDialogProviderProps {
  children: ReactNode;
}

export const ConfirmationDialogProvider: React.FC<
  ConfirmationDialogProviderProps
> = ({ children }) => {
  const [state, setState] = useState<ConfirmationState>({
    isOpen: false,
    title: '',
    description: '',
    variant: 'neutral',
    isLoading: false,
  });

  const showDialog = useCallback(
    (options: PromiseDialogOptions): Promise<boolean> => {
      return new Promise((resolve) => {
        setState({
          isOpen: true,
          title: options.title,
          description: options.description,
          variant: options.variant || 'neutral',
          confirmText: options.confirmText,
          cancelText: options.cancelText,
          isLoading: false,
          resolve,
        });
      });
    },
    [],
  );

  const showDialogWithCallback = useCallback(
    (options: CallbackDialogOptions): void => {
      setState({
        isOpen: true,
        title: options.title,
        description: options.description,
        variant: options.variant || 'neutral',
        confirmText: options.confirmText,
        cancelText: options.cancelText,
        loadingText: options.loadingText,
        isLoading: false,
        onConfirmCallback: options.onConfirm,
      });
    },
    [],
  );

  const handleConfirm = useCallback(async () => {
    if (state.onConfirmCallback) {
      setState((prev) => ({ ...prev, isLoading: true }));
      try {
        await state.onConfirmCallback();
        setState((prev) => ({
          ...prev,
          isOpen: false,
          isLoading: false,
          onConfirmCallback: undefined,
        }));
      } catch (error) {
        setState((prev) => ({ ...prev, isLoading: false }));
        console.error('Dialog callback error:', error);
      }
    } else {
      state.resolve?.(true);
      setState((prev) => ({
        ...prev,
        isOpen: false,
        isLoading: false,
        resolve: undefined,
      }));
    }
  }, [state.resolve, state.onConfirmCallback]);

  const handleCancel = useCallback(() => {
    if (state.onConfirmCallback) {
      setState((prev) => ({
        ...prev,
        isOpen: false,
        isLoading: false,
        onConfirmCallback: undefined,
      }));
    } else {
      state.resolve?.(false);
      setState((prev) => ({
        ...prev,
        isOpen: false,
        isLoading: false,
        resolve: undefined,
      }));
    }
  }, [state.resolve, state.onConfirmCallback]);

  const handleOpenChange = useCallback(
    (open: boolean) => {
      if (!open && !state.isLoading) {
        if (state.onConfirmCallback) {
          setState((prev) => ({
            ...prev,
            isOpen: false,
            onConfirmCallback: undefined,
          }));
        } else {
          state.resolve?.(false);
          setState((prev) => ({
            ...prev,
            isOpen: false,
            resolve: undefined,
          }));
        }
      }
    },
    [state.resolve, state.onConfirmCallback, state.isLoading],
  );

  const contextValue: ConfirmationDialogContextType = {
    showDialog,
    showDialogWithCallback,
  };

  return (
    <ConfirmDialogContext.Provider value={contextValue}>
      {children}
      <ConfirmDialog
        open={state.isOpen}
        onOpenChange={handleOpenChange}
        title={state.title}
        description={state.description}
        confirmText={state.confirmText}
        cancelText={state.cancelText}
        loadingText={state.loadingText}
        variant={state.variant}
        isLoading={state.isLoading}
        onConfirm={handleConfirm}
        onCancel={handleCancel}
      />
    </ConfirmDialogContext.Provider>
  );
};

export const useConfirmationDialogContext =
  (): ConfirmationDialogContextType => {
    const context = useContext(ConfirmDialogContext);
    if (context === undefined) {
      throw new Error(
        'useConfirmationDialogContext must be used within a ConfirmationDialogProvider',
      );
    }
    return context;
  };
